<template>
  <div>
    <v-layout align-center justify-space-between class="mb-3">
      <span class="title grey--text text--darken-2">Pending Listings</span>
      <v-layout align-center shrink>
        <v-btn small flat :loading="pdfLoading" @click="downloadPdf">
          <v-icon left small>picture_as_pdf</v-icon> PDF Report
        </v-btn>
        <v-btn icon small :loading="loading" @click="load">
          <v-icon>refresh</v-icon>
        </v-btn>
      </v-layout>
    </v-layout>

    <div v-if="loading" class="text-xs-center pa-4">
      <v-progress-circular indeterminate color="secondary" />
    </div>

    <v-alert v-else-if="!listings.length" type="success" outline>
      No listings are currently pending review.
    </v-alert>

    <v-card v-for="listing in listings" :key="listing.id" class="mb-4 pa-3">
      <v-layout wrap align-start>
        <v-flex grow>
          <v-layout align-center class="mb-1">
            <span class="subheading font-weight-medium mr-2">{{
              listing.title
            }}</span>
            <v-chip
              small
              :color="
                listing.status === 'pending_new'
                  ? 'blue lighten-4'
                  : 'orange lighten-4'
              "
              :text-color="
                listing.status === 'pending_new'
                  ? 'blue darken-2'
                  : 'orange darken-2'
              "
            >
              {{
                listing.status === "pending_new"
                  ? "New Listing"
                  : "Edit Request"
              }}
            </v-chip>
          </v-layout>

          <div class="body-2 grey--text">
            <template
              v-if="listing.provider_names && listing.provider_names.length"
            >
              Submitted by
              <strong>{{ listing.provider_names.join(", ") }}</strong>
              &bull; {{ listing.provider_emails.join(", ") }}
            </template>
            <template v-else
              >Submitted by Admin</template
            >
          </div>
          <div class="caption grey--text mt-1">
            Waiting since {{ formatDate(listing.status_updated_at) }}
          </div>

          <!-- For edits: show the proposed changes alongside live values -->
          <template
            v-if="
              listing.status === 'pending_edit' && listing.pending_edit_data
            "
          >
            <v-divider class="my-2" />
            <div class="caption font-weight-bold mb-1">Proposed changes:</div>
            <v-layout wrap>
              <template v-for="(val, key) in editDiff(listing)">
                <v-flex xs12 sm6 :key="key" class="mb-1">
                  <span class="caption grey--text text--darken-1"
                    >{{ key }}:
                  </span>
                  <span
                    class="body-2 red--text text--darken-1 mr-1"
                    style="text-decoration:line-through"
                    >{{ val.from }}</span
                  >
                  <v-icon x-small color="green">arrow_forward</v-icon>
                  <span class="body-2 green--text text--darken-1 ml-1">{{
                    val.to
                  }}</span>
                </v-flex>
              </template>
            </v-layout>
          </template>

          <!-- For new listings: show summary -->
          <template v-else-if="listing.status === 'pending_new'">
            <v-divider class="my-2" />
            <div class="body-2">{{ listing.description | truncate }}</div>
            <div v-if="listing.website" class="body-2 mt-1">
              <strong>Website:</strong>
              <a :href="listing.website" target="_blank">{{
                listing.website
              }}</a>
            </div>
          </template>
        </v-flex>

        <v-flex shrink class="ml-3 mt-1">
          <v-btn
            small
            depressed
            color="success"
            class="white--text mb-1"
            :loading="actionLoading === listing.id + '-approve'"
            @click="approve(listing)"
            >Approve</v-btn
          >
          <br />
          <v-btn
            small
            depressed
            color="error"
            class="white--text"
            :loading="actionLoading === listing.id + '-deny'"
            @click="openDenyDialog(listing)"
            >Deny</v-btn
          >
        </v-flex>
      </v-layout>
    </v-card>

    <!-- Deny dialog -->
    <v-dialog v-model="denyDialog" max-width="500px">
      <v-card class="pa-4">
        <v-card-title class="headline secondary--text pb-2"
          >Deny Listing</v-card-title
        >
        <v-card-text>
          <p>
            Optionally provide a reason. The provider will be notified by email
            if a reason is given.
          </p>
          <v-textarea
            v-model="denyReason"
            label="Reason (optional)"
            outlined
            rows="3"
            auto-grow
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn flat :disabled="denyLoading" @click="denyDialog = false"
            >Cancel</v-btn
          >
          <v-btn
            depressed
            color="error"
            class="white--text"
            :loading="denyLoading"
            @click="confirmDeny"
          >
            Deny
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top>
      {{ snackbar.message }}
      <v-btn flat @click="snackbar.show = false">Close</v-btn>
    </v-snackbar>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";

const baseUrl =
  (window.location.hostname === "localhost"
    ? "http://" + window.location.hostname + ":3000"
    : window.location.origin) + "/api";

const DISPLAY_FIELDS = [
  "title",
  "description",
  "address",
  "phone",
  "email",
  "website",
  "onbase",
  "event_on"
];

export default {
  name: "AdminPendingListings",
  filters: {
    truncate(v) {
      return v && v.length > 150 ? v.slice(0, 150) + "…" : v;
    }
  },
  data() {
    return {
      listings: [],
      loading: false,
      actionLoading: null,
      pdfLoading: false,
      denyDialog: false,
      denyReason: "",
      denyTarget: null,
      denyLoading: false,
      snackbar: { show: false, message: "", color: "success" }
    };
  },
  mounted() {
    this.load();
  },
  methods: {
    async load() {
      this.loading = true;
      try {
        this.listings = await this.$store.dispatch("fetchPendingListings");
      } catch {
        this.notify("Failed to load pending listings.", "error");
      } finally {
        this.loading = false;
      }
    },

    editDiff(listing) {
      const proposed = listing.pending_edit_data || {};
      const diff = {};
      for (const key of DISPLAY_FIELDS) {
        const from = String(listing[key] != null ? listing[key] : "");
        const to = String(proposed[key] != null ? proposed[key] : "");
        if (from !== to) diff[key] = { from, to };
      }
      return diff;
    },

    async approve(listing) {
      this.actionLoading = `${listing.id}-approve`;
      try {
        await this.$store.dispatch("approvePendingListing", listing.id);
        this.listings = this.listings.filter(l => l.id !== listing.id);
        this.notify(`"${listing.title}" approved.`);
      } catch {
        this.notify("Failed to approve listing.", "error");
      } finally {
        this.actionLoading = null;
      }
    },

    openDenyDialog(listing) {
      this.denyTarget = listing;
      this.denyReason = "";
      this.denyDialog = true;
    },

    async confirmDeny() {
      const { id, title } = this.denyTarget;
      this.denyLoading = true;
      try {
        await this.$store.dispatch("denyPendingListing", {
          id,
          reason: this.denyReason || undefined
        });
        this.listings = this.listings.filter(l => l.id !== id);
        this.notify(`"${title}" denied.`);
        this.denyDialog = false;
      } catch {
        this.notify("Failed to deny listing.", "error");
      } finally {
        this.denyLoading = false;
      }
    },

    async downloadPdf() {
      this.pdfLoading = true;
      try {
        const res = await axios.get(`${baseUrl}/listings/pending/pdf`, {
          headers: this.$store.getters.headers,
          responseType: "blob"
        });
        const url = URL.createObjectURL(
          new Blob([res.data], { type: "application/pdf" })
        );
        const a = document.createElement("a");
        a.href = url;
        a.download = `pending-listings-${
          new Date().toISOString().split("T")[0]
        }.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      } catch {
        this.notify("Could not generate PDF report.", "error");
      } finally {
        this.pdfLoading = false;
      }
    },

    formatDate(d) {
      return moment(d).format("MMM D, YYYY h:mm A");
    },
    notify(message, color = "success") {
      this.snackbar = { show: true, message, color };
    }
  }
};
</script>
